/**
 * チケットデザインテンプレート
 * HTML/CSSベースのテンプレートを定義
 */

/**
 * フリー入力欄のテキストを処理
 * @param {string} text - 入力テキスト
 * @param {boolean} isHtml - HTMLモードかどうか
 * @returns {string} 処理済みテキスト
 */
export const processSeatInfo = (text, isHtml = false) => {
  if (!text) return '';
  if (isHtml) {
    // HTMLモード: そのまま返す
    return text;
  } else {
    // プレーンテキストモード: HTMLタグをエスケープしてから改行を<br>に変換
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
    return escaped.replace(/\n/g, '<br>');
  }
};

/**
 * 利用可能なチケットサイズ
 */
export const ticketSizes = {
  'standard': {
    id: 'standard',
    name: '標準チケット',
    description: '一般的なチケットサイズ (210mm x 99mm)',
    width: '210mm',
    height: '99mm',
    orientation: 'landscape'
  },
  'mobile': {
    id: 'mobile',
    name: '携帯用チケット',
    description: 'スマホ向け縦レイアウト (105mm x 148mm)',
    width: '105mm',
    height: '148mm',
    orientation: 'portrait'
  }
};

/**
 * テンプレート1: シンプル・モダン
 */
export const templateModern = (ticketData, size = 'standard') => {
  const ticketSize = ticketSizes[size] || ticketSizes.standard;
  const processedSeatInfo = processSeatInfo(ticketData.seatInfo, ticketData.isHtmlMode);
  // CSVのfreeTextが優先、なければseatInfoを使用
  // freeTextもisHtmlModeに従って処理
  const processedFreeText = ticketData.freeText ? processSeatInfo(ticketData.freeText, ticketData.isHtmlMode) : '';
  const displayText = processedFreeText || processedSeatInfo;
  
  return `
  <div style="
    width: ${ticketSize.width};
    height: ${ticketSize.height};
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    padding: 20px;
    box-sizing: border-box;
    font-family: 'Arial', sans-serif;
    color: white;
    position: relative;
    overflow: hidden;
  ">
    <!-- 背景装飾 -->
    <div style="
      position: absolute;
      top: -50px;
      right: -50px;
      width: 200px;
      height: 200px;
      background: rgba(255,255,255,0.1);
      border-radius: 50%;
    "></div>
    
    <!-- ヘッダー -->
    <div style="margin-bottom: 15px;">
      <h1 style="
        margin: 0;
        font-size: 28px;
        font-weight: bold;
        text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
      ">${ticketData.eventName}</h1>
      <p style="
        margin: 5px 0 0 0;
        font-size: 14px;
        opacity: 0.9;
      ">${ticketData.eventDate || ''}</p>
    </div>
    
    <!-- メインコンテンツ -->
    <div style="
      display: flex;
      justify-content: space-between;
      align-items: center;
    ">
      <!-- 左側: チケット情報 -->
      <div style="flex: 1;">
        <div style="margin-bottom: 10px;">
          <p style="
            margin: 0;
            font-size: 12px;
            opacity: 0.8;
          ">チケット番号</p>
          <p style="
            margin: 5px 0 0 0;
            font-size: 20px;
            font-weight: bold;
            letter-spacing: 2px;
          ">#${ticketData.ticketNumber}</p>
        </div>
        
        ${ticketData.holderName ? `
        <div style="margin-bottom: 10px;">
          <p style="
            margin: 0;
            font-size: 12px;
            opacity: 0.8;
          ">お名前</p>
          <p style="
            margin: 5px 0 0 0;
            font-size: 16px;
            font-weight: bold;
          ">${ticketData.holderName}</p>
        </div>
        ` : ''}
        
        ${displayText ? `
        <div style="margin-top: 10px;">
          <p style="
            margin: 0;
            font-size: 12px;
            opacity: 0.8;
          ">備考</p>
          <p style="
            margin: 5px 0 0 0;
            font-size: 14px;
          ">${displayText}</p>
        </div>
        ` : ''}
      </div>
      
      <!-- 右側: QRコード -->
      <div style="
        background: white;
        padding: 15px;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0,0,0,0.2);
      ">
        <img src="${ticketData.qrCodeDataUrl}" style="
          width: 120px;
          height: 120px;
          display: block;
        " />
      </div>
    </div>
    
    <!-- フッター -->
    <div style="
      position: absolute;
      bottom: 15px;
      left: 20px;
      right: 20px;
      font-size: 10px;
      opacity: 0.7;
      text-align: center;
    ">
      入場時にこのQRコードをスキャンしてください
    </div>
  </div>
`;
};

/**
 * テンプレート2: クラシック・エレガント
 */
export const templateClassic = (ticketData, size = 'standard') => {
  const ticketSize = ticketSizes[size] || ticketSizes.standard;
  const processedSeatInfo = processSeatInfo(ticketData.seatInfo, ticketData.isHtmlMode);
  // CSVのfreeTextが優先、なければseatInfoを使用
  // freeTextもisHtmlModeに従って処理
  const processedFreeText = ticketData.freeText ? processSeatInfo(ticketData.freeText, ticketData.isHtmlMode) : '';
  const displayText = processedFreeText || processedSeatInfo;
  
  return `
  <div style="
    width: ${ticketSize.width};
    height: ${ticketSize.height};
    background: #f8f9fa;
    border: 3px solid #2c3e50;
    border-radius: 8px;
    padding: 20px;
    box-sizing: border-box;
    font-family: 'Georgia', serif;
    color: #2c3e50;
    position: relative;
  ">
    <!-- 装飾ボーダー -->
    <div style="
      position: absolute;
      top: 10px;
      left: 10px;
      right: 10px;
      bottom: 10px;
      border: 1px solid #bdc3c7;
      border-radius: 4px;
    "></div>
    
    <!-- コンテンツ -->
    <div style="position: relative; z-index: 1;">
      <!-- ヘッダー -->
      <div style="
        text-align: center;
        border-bottom: 2px solid #2c3e50;
        padding-bottom: 15px;
        margin-bottom: 20px;
      ">
        <h1 style="
          margin: 0;
          font-size: 32px;
          font-weight: normal;
          letter-spacing: 3px;
        ">${ticketData.eventName}</h1>
        ${ticketData.eventDate ? `
        <p style="
          margin: 10px 0 0 0;
          font-size: 14px;
          font-style: italic;
        ">${ticketData.eventDate}</p>
        ` : ''}
      </div>
      
      <!-- メインコンテンツ -->
      <div style="
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      ">
        <!-- 左側: チケット情報 -->
        <div style="flex: 1;">
          <table style="
            width: 100%;
            border-collapse: collapse;
          ">
            <tr>
              <td style="
                padding: 8px 0;
                font-size: 12px;
                color: #7f8c8d;
                width: 100px;
              ">チケット番号:</td>
              <td style="
                padding: 8px 0;
                font-size: 16px;
                font-weight: bold;
              ">#${ticketData.ticketNumber}</td>
            </tr>
            ${ticketData.holderName ? `
            <tr>
              <td style="
                padding: 8px 0;
                font-size: 12px;
                color: #7f8c8d;
              ">お名前:</td>
              <td style="
                padding: 8px 0;
                font-size: 16px;
                font-weight: bold;
              ">${ticketData.holderName}</td>
            </tr>
            ` : ''}
            ${displayText ? `
            <tr>
              <td style="
                padding: 8px 0;
                font-size: 12px;
                color: #7f8c8d;
              ">備考:</td>
              <td style="
                padding: 8px 0;
                font-size: 14px;
              ">${displayText}</td>
            </tr>
            ` : ''}
          </table>
        </div>
        
        <!-- 右側: QRコード -->
        <div style="
          border: 2px solid #2c3e50;
          padding: 10px;
          background: white;
        ">
          <img src="${ticketData.qrCodeDataUrl}" style="
            width: 120px;
            height: 120px;
            display: block;
          " />
        </div>
      </div>
      
      <!-- フッター -->
      <div style="
        position: absolute;
        bottom: 25px;
        left: 30px;
        right: 30px;
        text-align: center;
        font-size: 10px;
        color: #7f8c8d;
        font-style: italic;
      ">
        このチケットは入場時に必要です。大切に保管してください。
      </div>
    </div>
  </div>
`;
};

/**
 * テンプレート3: ポップ・カラフル
 */
export const templatePop = (ticketData, size = 'standard') => {
  const ticketSize = ticketSizes[size] || ticketSizes.standard;
  const processedSeatInfo = processSeatInfo(ticketData.seatInfo, ticketData.isHtmlMode);
  // CSVのfreeTextが優先、なければseatInfoを使用
  // freeTextもisHtmlModeに従って処理
  const processedFreeText = ticketData.freeText ? processSeatInfo(ticketData.freeText, ticketData.isHtmlMode) : '';
  const displayText = processedFreeText || processedSeatInfo;
  
  return `
  <div style="
    width: ${ticketSize.width};
    height: ${ticketSize.height};
    background: linear-gradient(45deg, #ff6b6b 0%, #feca57 50%, #48dbfb 100%);
    border-radius: 16px;
    padding: 25px;
    box-sizing: border-box;
    font-family: 'Arial', sans-serif;
    color: white;
    position: relative;
    overflow: hidden;
  ">
    <!-- 背景パターン -->
    <div style="
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: 
        radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%),
        radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%);
    "></div>
    
    <!-- コンテンツ -->
    <div style="position: relative; z-index: 1;">
      <!-- ヘッダー -->
      <div style="margin-bottom: 20px;">
        <h1 style="
          margin: 0;
          font-size: 36px;
          font-weight: 900;
          text-transform: uppercase;
          text-shadow: 3px 3px 0px rgba(0,0,0,0.2);
          letter-spacing: 2px;
        ">${ticketData.eventName}</h1>
        ${ticketData.eventDate ? `
        <p style="
          margin: 8px 0 0 0;
          font-size: 16px;
          font-weight: bold;
          text-shadow: 2px 2px 0px rgba(0,0,0,0.2);
        ">${ticketData.eventDate}</p>
        ` : ''}
      </div>
      
      <!-- メインコンテンツ -->
      <div style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;
      ">
        <!-- 左側: チケット情報 -->
        <div style="flex: 1;">
          <div style="
            background: rgba(255,255,255,0.3);
            backdrop-filter: blur(10px);
            border-radius: 12px;
            padding: 15px;
          ">
            <div style="margin-bottom: 12px;">
              <p style="
                margin: 0;
                font-size: 11px;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 1px;
              ">Ticket No.</p>
              <p style="
                margin: 5px 0 0 0;
                font-size: 24px;
                font-weight: 900;
                text-shadow: 2px 2px 0px rgba(0,0,0,0.2);
              ">#${ticketData.ticketNumber}</p>
            </div>
            
            ${ticketData.holderName ? `
            <div style="margin-bottom: 12px;">
              <p style="
                margin: 0;
                font-size: 11px;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 1px;
              ">Name</p>
              <p style="
                margin: 5px 0 0 0;
                font-size: 18px;
                font-weight: bold;
              ">${ticketData.holderName}</p>
            </div>
            ` : ''}
            
            ${displayText ? `
            <div style="margin-top: 12px;">
              <p style="
                margin: 0;
                font-size: 11px;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 1px;
              ">Note</p>
              <p style="
                margin: 5px 0 0 0;
                font-size: 14px;
              ">${displayText}</p>
            </div>
            ` : ''}
          </div>
        </div>
        
        <!-- 右側: QRコード -->
        <div style="
          background: white;
          padding: 20px;
          border-radius: 16px;
          box-shadow: 0 8px 16px rgba(0,0,0,0.3);
          transform: rotate(-3deg);
        ">
          <img src="${ticketData.qrCodeDataUrl}" style="
            width: 130px;
            height: 130px;
            display: block;
          " />
        </div>
      </div>
      
      <!-- フッター -->
      <div style="
        position: absolute;
        bottom: 30px;
        left: 25px;
        right: 25px;
        text-align: center;
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 1px;
        text-shadow: 2px 2px 0px rgba(0,0,0,0.2);
      ">
        ★ Scan QR Code at Entrance ★
      </div>
    </div>
  </div>
`;
};

/**
 * 利用可能なテンプレート一覧
 */
export const availableTemplates = [
  {
    id: 'modern',
    name: 'モダン',
    description: 'グラデーション背景のシンプルでモダンなデザイン',
    template: templateModern,
    preview: '/templates/modern-preview.png'
  },
  {
    id: 'classic',
    name: 'クラシック',
    description: 'エレガントで落ち着いたクラシックデザイン',
    template: templateClassic,
    preview: '/templates/classic-preview.png'
  },
  {
    id: 'pop',
    name: 'ポップ',
    description: 'カラフルで楽しいポップなデザイン',
    template: templatePop,
    preview: '/templates/pop-preview.png'
  }
];

/**
 * テンプレートIDからテンプレート関数を取得
 */
export const getTemplate = (templateId) => {
  const template = availableTemplates.find(t => t.id === templateId);
  return template ? template.template : templateModern;
};
